// style.jsx
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

export const StyledMDXEditor = styled(Box)`
  /* Hide MDXEditor's always-visible image action toolbar (delete + settings).
     We provide our own hover-shown Edit/Delete pill instead. */
  & [class*="editImageToolbar"] {
    display: none !important;
  }

  & .mdxeditor-toolbar {
    border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor.default};
    padding: 8px;
    background-color: ${({ theme }) => theme.palette.background.secondary};
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    border-radius: 8px 8px 0 0;
    border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
    border-bottom: none;

    button {
      padding: 4px 8px;
      color: ${({ theme }) => theme.palette.text.primary};
      background: ${({ theme }) => theme.palette.background.default};
      border: 1px solid ${({ theme }) => theme.palette.borderColor.dark};
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      min-height: 32px;
      border-radius: 4px;

      &[aria-checked="true"] {
        background-color: ${({ theme }) => theme.palette.background.dark};
      }

      &:hover {
        background-color: ${({ theme }) => theme.palette.action.hover};
      }
      &:disabled {
        opacity: 0.5;
        cursor: default;
      }

      svg {
        width: 16px;
        height: 16px;
        color: ${({ theme }) => theme.palette.text.primary};
      }
    }

    select {
      padding: ${({ theme }) => theme.spacing(0.5, 1)};
      border-radius: ${({ theme }) => theme.shape.borderRadius};
      border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
      background-color: ${({ theme }) => theme.palette.background.paper};
      color: ${({ theme }) => theme.palette.text.primary};
      cursor: pointer;
      height: 32px;

      &:hover {
        border-color: ${({ theme }) => theme.palette.primary.main};
      }
    }

    .mdxeditor-toolbar-group {
      display: flex;
      gap: ${({ theme }) => theme.spacing(0.5)};
      align-items: center;

      &:not(:last-child)::after {
        content: "";
        height: 24px;
        width: 1px;
        background-color: ${({ theme }) => theme.palette.borderColor.default};
        margin: 0 ${({ theme }) => theme.spacing(1)};
      }
    }
  }

  & .mui-mdx-editor {
    padding: 10px;
    border-radius: 0 0 8px 8px;
    border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
    min-height: 200px;
    height: 100%;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: ${({ theme }) => theme.typography.body1.fontSize};
    line-height: ${({ theme }) => theme.typography.body1.lineHeight};
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.background.default};
    cursor: text;

    /* Add these new styles for the content editable area */
    & [contenteditable="true"] {
      outline: none;
      min-height: 200px;
      height: 100%;
      width: 100%;
      display: block;

      &:empty:before {
        content: "Type something...";
        color: ${({ theme }) => theme.palette.text.disabled};
        position: absolute;
        pointer-events: none;
      }
    }

    /* Markdown content styles */
    & .mdxeditor-content-editable {
      position: relative;
      width: 100%;
      min-height: 200px;

      &:empty:after {
        content: "";
        display: inline-block;
        width: 1px;
        height: 1em;
        background-color: ${({ theme }) => theme.palette.text.primary};
        animation: blink 1s step-end infinite;
      }
    }

    &:focus-within {
      outline: none;
      border-color: ${({ theme }) => theme.palette.borderColor.dark};
    }

    h1 {
      ${({ theme }) => ({ ...theme.typography.h1 })};
      margin-bottom: ${({ theme }) => theme.spacing(2)};
    }

    h2 {
      ${({ theme }) => ({ ...theme.typography.h2 })};
      margin-bottom: ${({ theme }) => theme.spacing(2)};
    }

    h3 {
      ${({ theme }) => ({ ...theme.typography.h3 })};
      margin-bottom: ${({ theme }) => theme.spacing(1.5)};
    }

    p {
      margin-bottom: ${({ theme }) => theme.spacing(1.5)};
      min-height: 1.5em;

      &:empty::after {
        content: "";
      }
    }

    ul,
    ol {
      padding-left: ${({ theme }) => theme.spacing(3)};
      margin-bottom: ${({ theme }) => theme.spacing(1.5)};
    }

    li {
      margin-bottom: ${({ theme }) => theme.spacing(0.5)};
    }

    code {
      font-family: monospace;
      background-color: ${({ theme }) => theme.palette.grey[100]};
      padding: ${({ theme }) => theme.spacing(0.25, 0.5)};
      color: ${({ theme }) => theme.palette.text.primary};
      border-radius: ${({ theme }) => theme.shape.borderRadius};
      font-size: 0.9em;
    }

    blockquote {
      border-left: 4px solid ${({ theme }) => theme.palette.grey[300]};
      padding-left: ${({ theme }) => theme.spacing(2)};
      margin: ${({ theme }) => theme.spacing(1, 0)};
      & * {
        color: ${({ theme }) => theme.palette.text.primary};
      }
    }

    a {
      color: ${({ theme }) => theme.palette.primary.main};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }

    img {
      max-width: 100%;
      height: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: ${({ theme }) => theme.spacing(2)};

      th,
      td {
        border: 1px solid ${({ theme }) => theme.palette.divider};
        padding: ${({ theme }) => theme.spacing(1)};
        text-align: left;
      }

      th {
        background-color: ${({ theme }) => theme.palette.grey[50]};
        font-weight: ${({ theme }) => theme.typography.fontWeightBold};
      }
    }

    /* Markdown preview styles */
    .mdxeditor-preview {
      padding: ${({ theme }) => theme.spacing(2)};
    }

    /* Code block styles */
    .mdxeditor-codeblock {
      background-color: ${({ theme }) => theme.palette.grey[100]};
      border-radius: ${({ theme }) => theme.shape.borderRadius};
      padding: ${({ theme }) => theme.spacing(2)};
      margin: ${({ theme }) => theme.spacing(2, 0)};
      font-family: monospace;
    }
  }

  .cm-editor {
    background-color: ${({ theme }) => theme.palette.background.default};
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .cm-line {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .cm-gutters {
    background-color: ${({ theme }) => theme.palette.background.default};
    border-right-color: ${({ theme }) => theme.palette.divider};
  }

  .cm-activeLineGutter,
  .cm-activeLine {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  /* Blinking cursor animation */
  @keyframes blink {
    from,
    to {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;
